<template>
  <register-layout
    class="register-page"
    :enable-scroll-event="true"
    :disable-back="shouldDisableBack"
    @back="handleBack"
  >
    <InfoModal :is-open="showInfoModal" @close="showInfoModal = false" />
    <RegisterBio v-if="register.stage == registerStage.FILL_BIO" @on-info="showInfoModal = true" />
    <RegisterContact v-else-if="register.stage == registerStage.FILL_CONTACT" />
    <RegisterStudent v-else-if="register.stage == registerStage.FILL_LIFESTAGE" />
    <Completed v-else-if="register.stage == registerStage.COMPLETED" />
  </register-layout>
</template>

<script>
import '../../components/RegisterPage/register.scss';
import { mapState } from 'vuex';
import { REGISTER_STAGE } from '@/store/modules/profile';
import { getStageConfig, BACK_ACTION_TYPE } from '@/constants/register/stageConfig';
import RegisterLayout from '@/components/RegisterLayout';
import RegisterBio from '@/components/RegisterPage/RegisterBio';
import RegisterContact from '@/components/RegisterPage/RegisterContact';
import RegisterStudent from '@/components/RegisterPage/RegisterStudent';
import Completed from '@/components/RegisterPage/Completed';
import InfoModal from '@/components/MyGiving/Common/InfoModal.vue';
import navigationService from '@/utils/navigation-service';
import Logout from '@/mixins/Logout.js';
import { auth0 } from '@/utils/auth0';
import ONBOARDING_STATUS from '@/constants/ONBOARDING_STATUS';
import {
  trackRegistrationCompleted,
  trackRegistrationStarted,
  trackRegistrationStepViewed,
} from '@/utils/analytics';

// TODO: Clean up and refactor onboarding flow for Vuex store and components
export default {
  components: {
    RegisterBio,
    RegisterContact,
    RegisterStudent,
    Completed,
    RegisterLayout,
    InfoModal,
  },
  data() {
    return {
      showInfoModal: false,
      hasTrackedOnboardingStart: false,
      hasTrackedOnboardingCompletion: false,
      trackedOnboardingStages: {},
    };
  },
  mixins: [Logout],
  computed: {
    ...mapState('profile', ['register', 'hasRegistered']),
    ...mapState('user', ['token']),
    registerStage() {
      return REGISTER_STAGE;
    },
    currentStageConfig() {
      return getStageConfig(this.register.stage);
    },
    shouldDisableBack() {
      if (this.currentStageConfig.shouldDisableBack) {
        return this.currentStageConfig.shouldDisableBack(this.hasRegistered);
      }
      return false;
    },
  },
  watch: {
    'register.stage'(newStage, oldStage) {
      if (newStage === oldStage) return;
      this.trackOnboardingStage(newStage);
    },
  },

  async ionViewWillEnter() {
    // Check if user is authenticated before allowing access to register page
    if (!auth0.isAuthenticated.value) {
      this.$router.replace('/login');
      return;
    }

    // Restore registration stage on page reload
    // First, get the onboard status to restore the registration progress
    try {
      // Use non-blocking check - load from cache first, sync in background
      await this.$store.dispatch('profile/getOnboardStatus', {
        useCache: true,
        backgroundSync: true,
      });

      // Check if user has completed onboarding
      if (this.hasRegistered === ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED) {
        // User has completed onboarding, redirect to homepage
        this.$router.replace('/home');
        return;
      }
    } catch (e) {
      // If getOnboardStatus fails, logout user to restart the journey
      console.error('Error getting onboard status:', e);
      // Only logout if it's not a 401 (which would already trigger logout)
      if (!e.response || e.response.status !== 401) {
        this.logoutUser();
        return;
      }
      // For 401, let the global error handler handle it
      return;
    }

    // Fetch profile data to ensure it's loaded
    try {
      await this.$store.dispatch('profile/fetchProfile');
    } catch (e) {
      // If fetchProfile fails, continue - user might be mid-registration
      console.error('Error fetching profile:', e);
    }

    // Register custom back handler for swipe navigation
    navigationService.registerBackHandler(() => {
      this.handleBack();
    }, this.$route.path);

    // Register custom forward handler for swipe navigation
    navigationService.registerForwardHandler(() => {
      this.handleForward();
    }, this.$route.path);

    this.trackOnboardingStage(this.register.stage);
  },

  ionViewWillLeave() {
    // Clear custom handlers when leaving the page
    navigationService.clearBackHandler();
    navigationService.clearForwardHandler();
  },

  methods: {
    trackOnboardingStage(stage) {
      if (!this.hasTrackedOnboardingStart) {
        trackRegistrationStarted({
          flow: 'authenticated-onboarding',
          source: 'register-page',
        });
        this.hasTrackedOnboardingStart = true;
      }

      const stageConfig = getStageConfig(stage);
      const stageName = stageConfig?.name || String(stage);

      if (!this.trackedOnboardingStages[stage]) {
        trackRegistrationStepViewed({
          flow: 'authenticated-onboarding',
          step: stage,
          stepName: stageName,
          stepNumber: stageConfig?.stepNumber,
          stepTotal: stageConfig?.stepTotal,
          source: 'register-page',
        });
        this.trackedOnboardingStages[stage] = true;
      }

      if (stage === REGISTER_STAGE.COMPLETED && !this.hasTrackedOnboardingCompletion) {
        trackRegistrationCompleted({
          flow: 'authenticated-onboarding',
          source: 'register-page',
        });
        this.hasTrackedOnboardingCompletion = true;
      }
    },
    handleBack() {
      if (this.shouldDisableBack) {
        return;
      }

      const config = this.currentStageConfig;

      switch (config.backActionType) {
        case BACK_ACTION_TYPE.ROUTER:
          this.logoutUser();
          break;

        case BACK_ACTION_TYPE.STAGE:
          this.$store.dispatch('profile/inputRegisterForm', {
            key: 'stage',
            value: config.previousStage,
          });
          break;

        default:
          break;
      }
    },
    handleForward() {
      // Handle forward navigation for onboarding flow
      const currentStage = this.register.stage;

      if (currentStage === REGISTER_STAGE.FILL_BIO) {
        // Move to contact stage
        this.$store.dispatch('profile/inputRegisterForm', {
          key: 'stage',
          value: REGISTER_STAGE.FILL_CONTACT,
        });
      } else if (currentStage === REGISTER_STAGE.FILL_CONTACT) {
        // Move to life stage
        this.$store.dispatch('profile/inputRegisterForm', {
          key: 'stage',
          value: REGISTER_STAGE.FILL_LIFESTAGE,
        });
      } else if (currentStage === REGISTER_STAGE.FILL_LIFESTAGE) {
        // Move to next stage (if available)
        this.$store.dispatch('profile/inputRegisterForm', {
          key: 'stage',
          value: REGISTER_STAGE.COMPLETED,
        });
      } else {
        // At last stage or completed, no forward navigation available
        console.log('Onboarding: No forward navigation available (at last stage)');

        // Provide haptic feedback
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(50);
        }
      }
    },
  },
};
</script>
